import React, { useState, useEffect } from 'react';
import { fetchUser, updateUser, fetchChoices } from './api';
import './EditUserForm.css';

const EditUserForm = ({ userId, onUserUpdated, onCancel }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    initials: '',
    is_admin: false,
    default_contact: '',
    filter_jobs_by_default_contact: false
  });
  const [contactChoices, setContactChoices] = useState([]);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const userData = await fetchUser(userId);
        setFormData({
          username: userData.username || '',
          email: userData.email || '',
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          password: '',
          initials: userData.initials || '',
          is_admin: userData.is_admin || false,
          default_contact: userData.default_contact || '',
          filter_jobs_by_default_contact: userData.filter_jobs_by_default_contact || false
        });
      } catch (error) {
        console.error('Error loading user:', error);
        setError('Failed to load user data');
      } finally {
        setIsLoading(false);
      }
    };

    if (userId) {
      loadUser();
    }
  }, [userId]);

  useEffect(() => {
    const loadContactChoices = async () => {
      try {
        const choicesData = await fetchChoices();
        setContactChoices(choicesData.contact || []);
      } catch (error) {
        console.error('Error loading contact choices:', error);
      }
    };
    loadContactChoices();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      // Remove empty password from update data
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }

      await updateUser(userId, updateData);
      onUserUpdated();
    } catch (error) {
      console.error('Error updating user:', error);
      if (error.response?.data) {
        // Handle Django validation errors
        const errors = error.response.data;
        if (typeof errors === 'object') {
          const errorMessages = Object.values(errors).flat();
          setError(errorMessages.join(', '));
        } else {
          setError('Failed to update user');
        }
      } else {
        setError('Failed to update user');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="edit-user-form-overlay">
        <div className="edit-user-form">
          <div className="loading">Loading user data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-user-form-overlay">
      <div className="edit-user-form">
        <h3>Edit User</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">Username *</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="first_name">First Name</label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="last_name">Last Name</label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">New Password (leave blank to keep current)</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter new password"
            />
          </div>

          <div className="form-group">
            <label htmlFor="initials">Initials (Max 3 characters)</label>
            <input
              type="text"
              id="initials"
              name="initials"
              value={formData.initials}
              onChange={(e) => setFormData(prev => ({ ...prev, initials: e.target.value.toUpperCase() }))}
              maxLength={3}
            />
          </div>

          <div className="form-group">
            <label htmlFor="default_contact">Default Contact</label>
            <select
              id="default_contact"
              name="default_contact"
              value={formData.default_contact}
              onChange={handleChange}
            >
              <option value="">No default contact</option>
              {[...contactChoices]
                .sort((a, b) => a.order - b.order)
                .map(choice => (
                  <option key={choice.value} value={choice.value}>
                    {choice.display_name}
                  </option>
                ))}
              {formData.default_contact && !contactChoices.some(c => c.value === formData.default_contact) && (
                <option value={formData.default_contact}>{formData.default_contact}</option>
              )}
            </select>
          </div>

          <div className="form-group checkbox-group">
            <label htmlFor="filter_jobs_by_default_contact" className="checkbox-label">
              <input
                type="checkbox"
                id="filter_jobs_by_default_contact"
                name="filter_jobs_by_default_contact"
                checked={!!formData.default_contact && formData.filter_jobs_by_default_contact}
                onChange={handleChange}
                disabled={!formData.default_contact}
              />
              <span className="checkbox-text">Filter job list to default contact</span>
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label htmlFor="is_admin" className="checkbox-label">
              <input
                type="checkbox"
                id="is_admin"
                name="is_admin"
                checked={formData.is_admin}
                onChange={handleChange}
              />
              <span className="checkbox-text">Admin User</span>
            </label>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="form-buttons">
            <button type="submit" disabled={isSubmitting} className="update-btn">
              {isSubmitting ? 'Updating...' : 'Update User'}
            </button>
            <button type="button" onClick={onCancel} className="cancel-btn">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserForm;
